import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { liveTypes } from '../issue-types';
import { livePhases } from '../phases';
import { displayName } from '../people';
import { assistantsOf, defaultAssignee } from '../assistant';
import { cardCreateDefault } from '../company-role';
import { boardAssignees } from '../projects';
import { parseQuickAdd, quickAddSuggest, QUICK_ADD_FIELDS, type QuickAddSuggestion } from '../quick-add';
import { shortDay } from '../when';
import { PRIORITY_LABELS, SIZE_LABEL } from '../types';
import type { IssueStatus } from '../types';

/**
 * ── FAST CARD ENTRY — WS-3 ──────────────────────────────────────────────────
 *
 * A card should not take eight dropdowns to make. Type a title, press Enter, and
 * you are on the next one. Keyboard-first: after the title, tokens set fields
 * without leaving the line —
 *
 *     *type    @assignee    #phase    !priority    ~size    ^due
 *
 * — each resolved against what THIS board actually has (its types, its people,
 * its phases), shown as a chip as you type, and still editable in full on the
 * card afterwards. Everything else takes a sensible default — status "To do",
 * priority medium, and you as the assignee on a board you run — so the common
 * case really is a title and Enter, and nothing is ever locked in.
 *
 * ── THE PART THAT DOES NOT MAKE YOU REMEMBER THE KEY — 10 Sep 2026 ───────────
 *
 * Vinnie: *"the problem is I always forget the key … I wish I could click on,
 * for example when I start typing @ and then a name, I don't just see one name,
 * I see all the names that start with those letters and be able to click it and
 * select it — for the other fields as well, like size."* So while the caret sits
 * in a token, a menu lists what it could become — every match, not the one
 * guess — and clicking one writes it into the line. A bare `@` shows everyone; a
 * bare `~` shows every size. The key you half-remember shows its own answers.
 *
 * ⚠️ ONLY THE FIELDS THAT EXIST TODAY. Discipline (dev/network/security) and
 * dates are part of the plan work still to come; a token for a field the card
 * has no column for would be a promise the save cannot keep, so it is not here.
 * The dropdown card is untouched and remains the way to set everything else.
 * Parsing and the suggestions both live in ../quick-add.ts (pure, unit-tested);
 * this wires them to the board's real types/people/phases and to createIssue.
 */

/** The token the caret is inside, if it carries a menu — its sigil, the bit
 *  typed after it, and where it sits in the line so a pick can replace it. */
function activeToken(text: string, caret: number): { start: number; end: number; sigil: string; value: string } | null {
  let start = caret;
  while (start > 0 && !/\s/.test(text[start - 1])) start -= 1;
  let end = caret;
  while (end < text.length && !/\s/.test(text[end])) end += 1;
  const word = text.slice(start, end);
  if (!word) return null;
  const sigil = word[0];
  if (!(sigil in QUICK_ADD_FIELDS)) return null;
  return { start, end, sigil, value: word.slice(1) };
}

export default function QuickAdd({
  /* Force the menu open on a given token, for the screen checks — a static
     render cannot type or move a caret, so the menu would otherwise be the one
     part of this that nothing ever renders (the #301 lesson, as for the filter
     bar's openMenus). Never set in the app. */
  openOn,
}: { openOn?: { sigil: string; value: string } } = {}) {
  const issueTypes = useApp((s) => s.issueTypes);
  const phases = useApp((s) => s.phases);
  const access = useApp((s) => s.access);
  const profiles = useApp((s) => s.profiles);
  const meta = useApp((s) => s.meta);
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const groupsOf = useApp((s) => s.groupsOf);
  const createIssue = useApp((s) => s.createIssue);
  const meEmail = useApp((s) => s.user?.email ?? '');

  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [open, setOpen] = useState(true);
  const [hi, setHi] = useState(0);
  const [error, setError] = useState('');
  // Said once, after a card is added, when a token matched nothing — see commit().
  const [note, setNote] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const types = useMemo(() => liveTypes(issueTypes), [issueTypes]);
  const openPhases = useMemo(() => livePhases(phases), [phases]);
  /*
   * People first, then the assistants that have worked here — both can hold a
   * card, and the menu tells them apart with a quiet second line. An assistant
   * is stored as `assistant:<id>`, which createIssue and the card already read.
   */
  const people = useMemo(() => {
    /* Only people who can OPEN this board — not the whole workspace. Handing a
       card to somebody who cannot see the board assigns work to a person who can
       never pick it up; boardAssignees is the same test the switcher uses. */
    const onBoard = boardAssignees(
      projects.find((p) => p.id === projectId),
      access?.emails ?? [],
      access?.admins ?? [],
      groupsOf
    );
    const persons = [...new Set(onBoard.map((e) => e.toLowerCase()))].map((email) => ({
      email,
      name: displayName(email, profiles),
    }));
    const bots = assistantsOf(meta).map((a) => ({
      email: `assistant:${a.id}`,
      name: a.name?.trim() || a.id,
    }));
    return [...persons, ...bots];
  }, [access, profiles, meta, projects, projectId, groupsOf]);

  const world = useMemo(
    () => ({
      types: types.map((t) => ({ slug: t.slug, name: t.name })),
      people,
      phases: openPhases.map((p) => ({ id: p.id, name: p.name })),
    }),
    [types, people, openPhases]
  );

  const parsed = useMemo(() => parseQuickAdd(text, world), [text, world]);

  const token = openOn ? { start: 0, end: 0, ...openOn } : activeToken(text, caret);
  const suggestions: QuickAddSuggestion[] =
    (open || openOn) && token ? quickAddSuggest(token.sigil, token.value, world) : [];
  const tokenKey = token ? `${token.sigil}${token.value}` : '';
  useEffect(() => { setHi(0); }, [tokenKey]);
  const cur = suggestions.length ? Math.min(hi, suggestions.length - 1) : 0;

  const defaultKind = types[0]?.slug ?? 'task';
  // A quick-added card pre-fills both assignee slots from the creator's resolved
  // stance (company role + board/person override, src/company-role.ts): a manager
  // lands Accountable, a doer Responsible, and the sole assistant fills the doer
  // slot on an assistant-run board — so a typed card is owned the moment it lands.
  // Still editable: type @someone to hand the Accountable slot elsewhere; open the
  // card to name the doer.
  const meProfile = profiles[meEmail] ?? profiles[meEmail.toLowerCase()];
  const meDefault = cardCreateDefault({
    me: meEmail,
    personStance: meProfile?.cardDefaultStance,
    companyRole: meProfile?.companyRole,
    boardDefault: meta.cardDefaults,
    soleAssistant: defaultAssignee(meta),
  });
  // Who the preview chip names as the default owner: the doer if one is defaulted,
  // else the Accountable person (two-part assignee).
  const meDefaultWho = meDefault.responsible || meDefault.assignee;

  /** Put a chosen value into the line where the token is, and carry on typing. */
  function choose(s: QuickAddSuggestion) {
    const t = activeToken(text, caret);
    if (!t) return;
    const head = `${text.slice(0, t.start)}${t.sigil}${s.token} `;
    const next = head + text.slice(t.end).replace(/^\s+/, '');
    const pos = head.length;
    setText(next);
    setOpen(false);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) { el.focus(); el.setSelectionRange(pos, pos); }
      setCaret(pos);
    });
  }

  // One add per Enter. The line is only cleared AFTER createIssue resolves, so
  // without this a second Enter — or a double-click on Add — pressed during that
  // await re-reads the still-populated line and adds the same card again. A ref
  // flips synchronously (immune to React's async state batching), so the second
  // call returns at once instead of racing the create. Reset in finally.
  const submitting = useRef(false);

  async function commit() {
    if (submitting.current) return;
    const p = parsed;
    if (!p.title) return;
    submitting.current = true;
    setError('');
    try {
      const added = await createIssue({
        kind: p.kind?.slug ?? defaultKind,
        title: p.title,
        description: '',
        status: 'todo' as IssueStatus,
        priority: p.priority ?? 'medium',
        assignee: p.assignee?.email ?? meDefault.assignee,
        responsible: meDefault.responsible,
        labels: [],
        phase: p.phase?.id ?? null,
        size: p.size ?? undefined,
        area: null,
        dueDate: p.due,
      });
      /* The write was refused. Keep the line exactly as typed — including the
         parsed tokens — and say so, rather than clearing it and claiming it was
         added. L148. */
      if (!added) {
        setError('That could not be added — try again.');
        return;
      }
      /* A mistyped token (e.g. `!hgih`) is reported as a miss and deliberately
         NOT applied — quick-add never quietly changes the card. But the miss
         chips vanish with the line on a fast Enter, so say what was left off,
         once, right here, where it cannot be blown past. */
      setNote(
        p.misses.length
          ? `Added, but ${p.misses.map((m) => `“${m}”`).join(', ')} matched nothing here, so ${
              p.misses.length > 1 ? 'they were' : 'it was'
            } left off.`
          : ''
      );
      setText('');
      setCaret(0);
    } catch {
      setError('That could not be added — try again.');
    } finally {
      submitting.current = false;
    }
  }

  /* An assistant is stored as `assistant:<id>` and has no profile, so
     displayName would print the raw id — which is what showed as
     "assistant:codey" on the bar. Resolve its friendly name from the board's
     assistant list instead. */
  const nameOfAssignee = (email: string): string => {
    if (email.startsWith('assistant:')) {
      const id = email.slice('assistant:'.length);
      return assistantsOf(meta).find((a) => a.id === id)?.name?.trim() || id;
    }
    return displayName(email, profiles);
  };
  const meName = nameOfAssignee(meDefaultWho || (useApp.getState().user?.email ?? ''));
  const active = text.trim().length > 0;

  return (
    <div className="qadd">
      <div className="qadd-row">
        <span className="qadd-plus" aria-hidden="true">+</span>
        <input
          ref={inputRef}
          className="qadd-input"
          value={text}
          autoComplete="off"
          spellCheck={false}
          placeholder="Add a card…  then @who  #phase  !priority  *type  ~size  ^due"
          aria-label="Add a card"
          title="Type a title, then keys set fields — @ assignee, # phase, ! priority, * type, ~ size, ^ due date. A menu of choices appears; Enter adds the card."
          onChange={(e) => {
            setText(e.target.value);
            setCaret(e.target.selectionStart ?? e.target.value.length);
            setOpen(true);
            if (note) setNote('');
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? caret)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={(e) => {
            /* Enter ALWAYS adds the card — the one promise of this bar ("type a
               title, press Enter"). Picking a suggestion is Tab or a click, never
               Enter, so the menu being open never swallows the add. A half-typed
               token the user didn't pick is parsed as a miss and flagged, not
               quietly applied — see commit(). (Vinnie, 24 Oct 2026.) */
            if (suggestions.length) {
              if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => (h + 1) % suggestions.length); return; }
              if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => (h - 1 + suggestions.length) % suggestions.length); return; }
              if (e.key === 'Tab') { e.preventDefault(); choose(suggestions[cur]); return; }
              if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
            }
            if (e.key === 'Enter') { e.preventDefault(); void commit(); }
            else if (e.key === 'Escape') { setText(''); setCaret(0); setNote(''); }
          }}
        />
        <button
          type="button"
          className="btn btn-primary qadd-add"
          disabled={!parsed.title}
          onClick={() => void commit()}
        >
          Add
        </button>
      </div>

      {/* The menu of what this token could become — every match, click to pick. */}
      {suggestions.length > 0 && token && (
        <ul
          className="qadd-ac"
          role="listbox"
          aria-label={QUICK_ADD_FIELDS[token.sigil]}
          /* Keep the input focused so a click lands as a pick, not a blur. */
          onMouseDown={(e) => e.preventDefault()}
        >
          <li className="qadd-ac-head" aria-hidden="true">
            {QUICK_ADD_FIELDS[token.sigil]}
            <span className="qadd-ac-key">{token.sigil}</span>
          </li>
          {suggestions.map((s, i) => (
            <li key={`${s.token}-${i}`}>
              <button
                type="button"
                role="option"
                aria-selected={i === cur}
                className={`qadd-ac-row${i === cur ? ' is-hi' : ''}`}
                onClick={() => choose(s)}
              >
                <span className="qadd-ac-label">{s.label}</span>
                {s.sub && <span className="qadd-ac-sub">{s.sub}</span>}
              </button>
            </li>
          ))}
          <li className="qadd-ac-tip" aria-hidden="true">↑↓ to move · Tab or click to pick · Enter adds the card</li>
        </ul>
      )}

      {/* THE PREVIEW: a quiet line of what will be set, not a row of buttons. */}
      {active && (
        <div className="qadd-preview">
          <span className="qadd-preview-lead">Adds</span>
          {/* type (or the board default) */}
          <span className="qadd-chip is-type">{parsed.kind?.name ?? (types[0]?.name ?? 'Task')}</span>
          {/* status is always To do to start — a default you rarely change here */}
          <span className="qadd-chip is-default">To do</span>
          {/* assignee, or you by default */}
          {parsed.assignee ? (
            <span className="qadd-chip">{parsed.assignee.name}</span>
          ) : (
            <span className="qadd-chip is-default">{meDefaultWho ? meName : 'Unassigned'}</span>
          )}
          {parsed.phase && <span className="qadd-chip is-phase">{parsed.phase.name}</span>}
          {parsed.priority && (
            <span className={`qadd-chip is-pri pri-${parsed.priority}`}>{PRIORITY_LABELS[parsed.priority]}</span>
          )}
          {parsed.size && <span className="qadd-chip is-size">{SIZE_LABEL[parsed.size]}</span>}
          {parsed.due && <span className="qadd-chip is-phase">Due {shortDay(parsed.due)}</span>}
          {parsed.misses.map((m) => (
            <span key={m} className="qadd-chip is-miss" title="No match on this board — not applied">
              {m}
            </span>
          ))}
        </div>
      )}

      {/*
        * THE LEGEND STAYS PUT — Vinnie, 10 Sep 2026: *"the legend should be
        * static until people get the hang of the shortcuts. I want more popup
        * tips; people will need it."* So it no longer vanishes the moment you
        * type, and each key carries a tooltip spelling out what it does.
        */}
      <div className="qadd-legend">
        <span title="Type @ then a name to assign it — a menu of people and assistants appears to pick from.">
          <b>@</b> assignee
        </span>
        <span title="Type # to put the card in a stage of the plan.">
          <b>#</b> phase
        </span>
        <span title="Type ! to set how urgent it is — ! high, ! critical, and so on.">
          <b>!</b> priority
        </span>
        <span title="Type * to set what kind it is — task, bug, and whatever else this board uses.">
          <b>*</b> type
        </span>
        <span title="Type ~ for how big it is — ~ quick, ~ medium, ~ long.">
          <b>~</b> size
        </span>
        <span title="Type ^ to set a due date — ^ today, ^ fri, ^ +2w, ^ eom, or a date.">
          <b>^</b> due
        </span>
        <span className="qadd-legend-end">Type a key to see the choices · Enter to add</span>
      </div>

      {note && <div className="qadd-note" role="status">{note}</div>}
      {error && <div className="qadd-error" role="alert">{error}</div>}
    </div>
  );
}
